/**
 * Quiz Choices Utilities
 *
 * This module provides functionality to automatically assign scores to quiz options
 * based on their labels. It handles various scale types including:
 * - Numbered options (e.g., "1 - Agree", "2 - Disagree")
 * - Likert scales (agree/disagree, satisfied/dissatisfied, likely/unlikely, etc.)
 * - Special options that should always score 0 (N/A, Other, Don't know, etc.)
 *
 * The module detects the sentiment direction (positive-first vs negative-first)
 * and assigns scores accordingly.
 */

export interface OptionWithValue {
  value: string;
  score?: number;
}

export interface OptionWithValueAndOid extends OptionWithValue {
  oid: string;
}

export interface Preset {
  key: string;
  name: string;
  options: OptionWithValue[];
}

// =============================================================================
// PRESET IDENTIFICATION
// =============================================================================

// Test the preset name for "NPS" or "Net Promoter Score"
export const REGEX_NPS = /^NPS|Net Promoter Score/i;

// Matches a number at the beginning of the string (e.g., "1", "2 - Agree")
export const REGEX_NUMBERED = /^(\d+)/;

// =============================================================================
// SPECIAL OPTIONS (score 0)
// =============================================================================

// Starts with "not applicable" or standalone "na"/"n/a"
export const REGEX_NOT_APPLICABLE = /^(not applicable|n\/?a)\b/i;

// Contains "don't know" or "unknown"
export const REGEX_DONT_KNOW = /don't know|unknown/i;

// Contains "other" as a standalone word
export const REGEX_OTHER = /\bother\b/i;

// Contains "prefer not to say"
export const REGEX_PREFER_NOT_TO_SAY = /prefer not to say/i;

// =============================================================================
// SCALE TYPE DETECTION
// =============================================================================

// Contains agreeable words like "agree" or "disagree"
export const REGEX_AGREEABLE = /agree|disagree/i;

// Contains "satisfied", "dissatisfied", or "unsatisfied"
export const REGEX_SATISFIED = /satisfied|dissatisfied|unsatisfied/i;

// Contains "likely" or "unlikely"
export const REGEX_LIKELY = /likely|unlikely/i;

// Contains "happy" or "unhappy"
export const REGEX_HAPPY = /happy|unhappy/i;

// Contains "sure" or "unsure"
export const REGEX_SURE = /\bsure\b|unsure/i;

// =============================================================================
// SENTIMENT DETECTION (Positive variants)
// =============================================================================

// Positive: "agree" (but not "disagree", "not agree", or "not at all agree")
const REGEX_POSITIVE_AGREE = /\bagree\b/i;
const REGEX_NEGATIVE_AGREE = /\bdisagree\b|\bnot\s+(at\s+all\s+)?agree\b/i;

// Positive: "satisfied" (but not "dissatisfied", "unsatisfied", "not satisfied", or "not at all satisfied")
const REGEX_POSITIVE_SATISFIED = /\bsatisfied\b/i;
const REGEX_NEGATIVE_SATISFIED =
  /\bdissatisfied\b|\bunsatisfied\b|\bnot\s+(at\s+all\s+)?satisfied\b/i;

// Positive: "likely" (but not "unlikely", "not likely", or "not at all likely")
const REGEX_POSITIVE_LIKELY = /\blikely\b/i;
const REGEX_NEGATIVE_LIKELY = /\bunlikely\b|\bnot\s+(at\s+all\s+)?likely\b/i;

// Positive: "happy" (but not "unhappy", "not happy", or "not at all happy")
const REGEX_POSITIVE_HAPPY = /\bhappy\b/i;
const REGEX_NEGATIVE_HAPPY = /\bunhappy\b|\bnot\s+(at\s+all\s+)?happy\b/i;

// Positive: "sure" (but not "unsure", "not sure", or "not at all sure")
const REGEX_POSITIVE_SURE = /\bsure\b/i;
const REGEX_NEGATIVE_SURE = /\bunsure\b|\bnot\s+(at\s+all\s+)?sure\b/i;

// =============================================================================
// SENTIMENT DETECTION (Negative variants)
// =============================================================================

// All negative patterns (disagree, dissatisfied, not satisfied, not at all satisfied, etc.)
const REGEX_NEGATIVE_ALL = new RegExp(
  [
    /\bdisagree\b/,
    /\bnot\s+(at\s+all\s+)?agree\b/,
    /\bdissatisfied\b/,
    /\bunsatisfied\b/,
    /\bnot\s+(at\s+all\s+)?satisfied\b/,
    /\bunlikely\b/,
    /\bnot\s+(at\s+all\s+)?likely\b/,
    /\bunhappy\b/,
    /\bnot\s+(at\s+all\s+)?happy\b/,
    /\bunsure\b/,
    /\bnot\s+(at\s+all\s+)?sure\b/,
  ]
    .map((r) => r.source)
    .join('|'),
  'i'
);

export function guessPresetScores(preset: Preset): OptionWithValue[] | null {
  const options = preset.options;

  if (options.length === 0) {
    return null;
  }

  // Check if NPS - typically uses 1-10 scale (or 0-10)
  // Try to extract numbers from labels first, otherwise use 1-indexed position
  if (REGEX_NPS.test(preset.name)) {
    return options.map((opt, idx) => {
      const match = REGEX_NUMBERED.exec(opt.value);
      const scoreStr = match?.[1];
      return {
        ...opt,
        score: scoreStr ? parseInt(scoreStr, 10) : idx + 1,
      };
    });
  }

  // Check if options are already numbered (e.g., "1", "2", "3" or "1 - Agree")
  const firstOption = options[0];
  if (firstOption) {
    const firstMatch = REGEX_NUMBERED.exec(firstOption.value);
    if (firstMatch) {
      const allNumbered = options.every((opt) =>
        REGEX_NUMBERED.test(opt.value)
      );
      if (allNumbered) {
        return options.map((opt) => {
          const match = REGEX_NUMBERED.exec(opt.value);
          const scoreStr = match?.[1];
          return {
            ...opt,
            score: scoreStr ? parseInt(scoreStr, 10) : 0,
          };
        });
      }
    }
  }

  // Separate special options (that should score 0) from regular options
  const specialIndices: number[] = [];
  const regularIndices: number[] = [];

  options.forEach((opt, idx) => {
    if (
      REGEX_NOT_APPLICABLE.test(opt.value) ||
      REGEX_OTHER.test(opt.value) ||
      REGEX_PREFER_NOT_TO_SAY.test(opt.value) ||
      REGEX_DONT_KNOW.test(opt.value)
    ) {
      specialIndices.push(idx);
    } else {
      regularIndices.push(idx);
    }
  });

  if (regularIndices.length === 0) {
    // Only special options, all get 0
    return options.map((opt) => ({ ...opt, score: 0 }));
  }

  // Determine scale type
  const isAgreeable = options.some((opt) => REGEX_AGREEABLE.test(opt.value));
  const isSatisfied = options.some((opt) => REGEX_SATISFIED.test(opt.value));
  const isLikely = options.some((opt) => REGEX_LIKELY.test(opt.value));
  const isHappy = options.some((opt) => REGEX_HAPPY.test(opt.value));
  const isSure = options.some((opt) => REGEX_SURE.test(opt.value));

  if (!isAgreeable && !isSatisfied && !isLikely && !isHappy && !isSure) {
    // Can't determine scoring mechanism
    return null;
  }

  // Determine direction: does it start with positive or negative end?
  const firstRegularIdx = regularIndices[0];
  if (firstRegularIdx === undefined) {
    return null;
  }

  const firstRegularOption = options[firstRegularIdx];
  if (!firstRegularOption) {
    return null;
  }

  const firstRegularValue = firstRegularOption.value;

  // Check if options have numbers that indicate direction
  // If first option starts with a low number and later options have higher numbers,
  // treat as ascending (negative-first conceptually)
  const firstNumberMatch = REGEX_NUMBERED.exec(firstRegularValue);
  let numberDirectionKnown = false;
  let isAscendingByNumber = false;

  if (firstNumberMatch) {
    const firstNumberStr = firstNumberMatch[1];
    if (firstNumberStr) {
      const firstNumber = parseInt(firstNumberStr, 10);
      // Check if there's a later option with a higher number
      for (let i = 1; i < regularIndices.length; i++) {
        const laterIdx = regularIndices[i];
        if (laterIdx !== undefined) {
          const laterOption = options[laterIdx];
          if (laterOption) {
            const laterNumberMatch = REGEX_NUMBERED.exec(laterOption.value);
            if (laterNumberMatch) {
              const laterNumberStr = laterNumberMatch[1];
              if (laterNumberStr) {
                const laterNumber = parseInt(laterNumberStr, 10);
                if (laterNumber > firstNumber) {
                  numberDirectionKnown = true;
                  isAscendingByNumber = true;
                  break;
                } else if (laterNumber < firstNumber) {
                  numberDirectionKnown = true;
                  isAscendingByNumber = false;
                  break;
                }
              }
            }
          }
        }
      }
    }
  }

  // Check if first option is positive (without negative counterparts)
  const hasPositive =
    (REGEX_POSITIVE_AGREE.test(firstRegularValue) &&
      !REGEX_NEGATIVE_AGREE.test(firstRegularValue)) ||
    (REGEX_POSITIVE_SATISFIED.test(firstRegularValue) &&
      !REGEX_NEGATIVE_SATISFIED.test(firstRegularValue)) ||
    (REGEX_POSITIVE_LIKELY.test(firstRegularValue) &&
      !REGEX_NEGATIVE_LIKELY.test(firstRegularValue)) ||
    (REGEX_POSITIVE_HAPPY.test(firstRegularValue) &&
      !REGEX_NEGATIVE_HAPPY.test(firstRegularValue)) ||
    (REGEX_POSITIVE_SURE.test(firstRegularValue) &&
      !REGEX_NEGATIVE_SURE.test(firstRegularValue));

  // Check if first option is negative
  const hasNegative = REGEX_NEGATIVE_ALL.test(firstRegularValue);

  // If we can't determine direction clearly, return null
  if (!numberDirectionKnown && !hasPositive && !hasNegative) {
    return null;
  }

  // Determine direction: use number direction if available, otherwise use sentiment
  const isPositiveFirst = numberDirectionKnown
    ? !isAscendingByNumber // If numbers ascend (1, 2, 3...), that's like negative-first
    : hasPositive && !hasNegative;

  // Assign scores to regular options
  const result: OptionWithValue[] = options.map((opt) => ({ ...opt }));
  const regularCount = regularIndices.length;

  regularIndices.forEach((idx, position) => {
    const option = result[idx];
    if (!option) {
      return;
    }

    let score: number;
    if (isPositiveFirst) {
      // Positive first: highest score for first option, descending
      score = regularCount - position;
    } else {
      // Negative first: lowest score for first option, ascending
      score = position + 1;
    }
    result[idx] = { ...option, score };
  });

  // Special options get 0
  specialIndices.forEach((idx) => {
    const option = result[idx];
    if (!option) {
      return;
    }
    result[idx] = { ...option, score: 0 };
  });

  return result;
}
